import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { CheckCircle2, Info, XCircle } from "lucide-react";
import { cn } from "../../lib/cn";

type ToastTone = "success" | "error" | "info";

type Toast = {
  id: string;
  title: string;
  description?: string;
  tone: ToastTone;
};

type ToastContextValue = {
  toast: (input: Omit<Toast, "id">) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((input: Omit<Toast, "id">) => {
    const id = crypto.randomUUID();
    setToasts((current) => [...current, { ...input, id }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((item) => item.id !== id));
    }, 4200);
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed right-4 top-4 z-50 flex w-[min(92vw,24rem)] flex-col gap-3" role="status" aria-live="polite">
        {toasts.map((item) => {
          const Icon = item.tone === "success" ? CheckCircle2 : item.tone === "error" ? XCircle : Info;
          return (
            <div
              key={item.id}
              className={cn(
                "app-surface animate-fade-up flex gap-3 rounded-card p-4 shadow-lift",
                item.tone === "error" && "border-[color:var(--danger)]",
                item.tone === "success" && "border-[color:var(--success)]"
              )}
            >
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--primary)]" aria-hidden />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[color:var(--text)]">{item.title}</p>
                {item.description ? <p className="mt-1 text-sm leading-6 text-[color:var(--muted)]">{item.description}</p> : null}
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside ToastProvider");
  return context;
}
